import { useRef, useState } from 'react';
import { downloadFile } from '../utilities/file-utils';

const ON_STARTED_DELAY = 1000;

const getBody = (body, bodyFormField) => {
  if (!body) return {};
  if (bodyFormField) {
    // Same as a classic form submit with a single field holding the JSON, for servers that read request.POST
    return {
      body: new URLSearchParams({ [bodyFormField]: JSON.stringify(body) }),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }
    };
  }
  return {
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json;charset=utf-8' }
  };
};

/**
 * Generic hook downloads file.
 * If the download is not responding (fail or succeed) within delay amount, onStarted will be triggerred.
 * Otherwise, onSucceeded or onFailed will be triggerred.
 * This can be helpful in case the onFail is immediately returned and user should not receive notification similar to "started ... "
 * */
export const useDownload = () => {
  const isDownloadingRef = useRef(false);
  const [isDownloading, setDownloading] = useState(false);

  const setIsDownloading = val => {
    isDownloadingRef.current = val;
    setDownloading(val);
  };

  /**
   * @param {string} method - the HTTP Method to be used.
   * @param {string} url - endpoint URL.
   * @param {object} body - request body.
   * @param {string} bodyFormField - optional. When set, the body is sent as a form with this single field holding the JSON, instead of a JSON body.
   * @param {string} fileName - the File name to be saved with.
   * @param {function} onStarted - optional argument that will be invoked after delay amount if the download didn't complete (fail/success) yet.
   * @param {function} onSucceeded - optional argument that will be invoked when file is successfully downloaded
   * @param {function} onFailed - optional argument that will be invoked when download fails
   * @param {string} credentials - default to same-origin. Can be one of 'include' | 'omit' | 'same-origin'
   * @param {string} cache - default to no-cache. Can be one of 'default' | 'force-cache' | 'no-cache' | 'no-store' | 'only-if-cached' | 'reload'
   * @param {string} mode - default to no-cors. Can be one of 'cors' | 'navigate' | 'no-cors' | 'same-origin'
   * */
  const download = ({
    method = 'get',
    url,
    body,
    bodyFormField,
    fileName,
    onStarted,
    onSucceeded,
    onFailed,
    onNoContent,
    cache = 'no-cache',
    mode = 'cors',
    credentials = 'same-origin'
  }) => {
    const startedHandler = () => {
      setIsDownloading(true);
      if (!onStarted) return;
      /** If by now (after delay) download is still loading, trigger onStarted
       * (in case a handler wants to show something advanced - loading snackbar) */
      setTimeout(() => isDownloadingRef.current === true && onStarted?.(), ON_STARTED_DELAY);
    };

    const successHandler = r => {
      setIsDownloading(false);
      onSucceeded?.(r);
    };

    const errorHandler = e => {
      setIsDownloading(false);
      onFailed?.(e);
    };

    const getContentDispositionFileName = contentDisposition => {
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";\s]+)/i);
        if (filenameMatch && filenameMatch[1]) {
          // Decode the filename if it's encoded
          return decodeURIComponent(filenameMatch[1].split("''").pop());
        }
      }
    };

    fetch(url, { method, credentials, cache, mode, ...getBody(body, bodyFormField) })
      .then(response => {
        if (response.status === 204 && onNoContent) {
          setIsDownloading(false);
          onNoContent();
        } else if (response instanceof Error || response.status >= 400) {
          // Keep the status on the error, so a handler can tell 503 (service down) from other faults.
          response
            .json()
            .then(errorBody => errorHandler({ ...errorBody, status: response.status }))
            .catch(() => errorHandler(response));
        } else {
          response
            .blob()
            .then(blob => {
              const blobUrl = URL.createObjectURL(blob);
              const fileLink = document.createElement('a');

              fileLink.href = blobUrl;
              fileLink.download =
                fileName || getContentDispositionFileName(response.headers.get('content-disposition'));
              fileLink.click();
              successHandler(blob);
              URL.revokeObjectURL(blobUrl);
            })
            .catch(errorHandler);
        }
      })
      .catch(errorHandler);

    startedHandler();
  };

  return { download, isDownloading };
};

export const useLinkDownload = () => {
  // currently - this is useless, as there is no real progress - but maybe we will find
  const [isDownloading, setIsDownloading] = useState(false);

  const download = ({ fileName, url, onStarted, onFinished, onFailed }) => {
    const startHandler = () => {
      setIsDownloading(true);
      onStarted?.();
    };

    const finishHandler = () => {
      setIsDownloading(false);
      onFinished?.();
    };

    const failHandler = e => {
      setIsDownloading(false);
      onFailed?.(e);
    };

    try {
      startHandler();
      downloadFile({ fileName, url });
      finishHandler();
    } catch (e) {
      failHandler(e);
    }
  };

  return { download, isDownloading };
};

/**
 * NOT RECOMMENDED - unless you really want it!
 * */
export const useExternalDownload = () => {
  /**
   * @param {string} method - the HTTP Method to be used.
   * @param {string} url - endpoint URL.
   * @param {object} body - request body.
   * */
  const download = ({ method = 'get', url, body }) => {
    const form = document.createElement('form');
    const input = document.createElement('input');

    form.setAttribute('method', method);
    form.setAttribute('action', url);
    form.setAttribute('target', '_blank');

    input.setAttribute('type', 'hidden');
    input.setAttribute('name', 'json');
    input.setAttribute('value', JSON.stringify(body));

    document.body.append(form);
    form.append(input);
    form.submit();

    form.remove();
  };

  return { download };
};
